import { addDays, format, formatDistanceToNow } from 'date-fns'
import csLocale from 'date-fns/locale/cs'
import React from 'react'

import { RcTableItem } from '../components/CuzkMonitorRcTable/CuzkMonitorRcTable'
import { DynamicSearchTooltip } from '../components/DynamicSearchTooltip/DynamicSearchTooltip'
import { RcInputError } from '../components/RcInputError/RcInputError'
import { RcNumberData } from '../components/RcNumberData/RcNumberData'
import { TableCellLabels } from '../components/TableCellLabels/TableCellLabels'
import { CheckFrequency } from './checkFrequency'
import { czDateFormatDay } from './dateFormat'
import { Identifier, RegisterGroupMap } from './dynamicSearchTooltip'
import { LabelConnectionFragment } from './labelsTypes'

export interface cuzkOs {
  id: string
  castobcenazev?: string | null
  cislodomovni?: number | null
  cisloorientacni?: string | null
  charostype?: number | null
  doplnekico?: string | null
  ico?: string | null
  jmeno?: string | null
  lastCheck?: Date | null
  mestskacastnazev?: string | null
  mestskyobvodnazev?: string | null
  nazev?: string | null
  obecnazev?: string | null
  okresnazev?: string | null
  prijmeni?: string | null
  psc?: string | null
  rc?: string | null
  rczkracene?: string | null
  statnazev?: string | null
  titulpred?: string | null
  titulza?: string | null
  typcpce?: string | null
  ulice?: string | null
  vlastniktyp?: string | null
}

export const labelRenderer = (
  labels: LabelConnectionFragment,
  record: RcTableItem
) => (
  <TableCellLabels
    labels={labels}
    labelEntityType="monitoringCuzkRcId"
    recordId={record.id}
    modalTitle={record.lastname}
  />
)

export const rcIcoRenderer = (_: string, record: RcTableItem) =>
  record.rc ? (
    <DynamicSearchTooltip
      identifier={{
        [Identifier.RC]: record.rc,
        [Identifier.FIRST_NAME]:
          record.firstname || record.cuzkOByCuzkOsId?.jmeno || undefined,
        [Identifier.LAST_NAME]:
          record.lastname || record.cuzkOByCuzkOsId?.prijmeni || undefined,
        [Identifier.DESCRIPTION]: record.description,
      }}
      identifierType={Identifier.RC}
      excludedRegisters={RegisterGroupMap.ČÚZK}
    >
      {record.rc}
    </DynamicSearchTooltip>
  ) : record.ico ? (
    <DynamicSearchTooltip
      identifier={{
        [Identifier.ICO]: record.ico,
        [Identifier.FIRST_NAME]:
          record.firstname || record.cuzkOByCuzkOsId?.jmeno || undefined,
        [Identifier.LAST_NAME]:
          record.lastname || record.cuzkOByCuzkOsId?.prijmeni || undefined,
        [Identifier.COMPANY_NAME]: record.cuzkOByCuzkOsId?.nazev || undefined,

        [Identifier.DESCRIPTION]: record.description,
      }}
      identifierType={Identifier.ICO}
      excludedRegisters={RegisterGroupMap.ČÚZK}
    >
      {record.ico}
    </DynamicSearchTooltip>
  ) : (
    <div className="text-gray-400 w-full text-center">---</div>
  )

export const fullnameRenderer = (_: string, record: RcTableItem) =>
  record.firstname && record.lastname ? (
    record.firstname + ' ' + record.lastname
  ) : record.cuzkOByCuzkOsId?.jmeno && record.cuzkOByCuzkOsId.prijmeni ? (
    record.cuzkOByCuzkOsId?.jmeno + ' ' + record.cuzkOByCuzkOsId.prijmeni
  ) : record.cuzkOByCuzkOsId?.nazev ? (
    record.cuzkOByCuzkOsId?.nazev
  ) : (
    <div className="text-gray-400 w-full text-center">---</div>
  )

export const validatedRenderer = (_: string, record: RcTableItem) =>
  record.ico ? (
    <div className="text-gray-400 text-center">IČO</div>
  ) : record.validatedCode === 30 ? (
    <RcNumberData icon rcNumber={record.rc} className="text-gray-700" />
  ) : (
    <RcInputError error={record.validatedCode || 0} />
  )

export const checkFrequencyRenderer = (checkFrequency?: number) =>
  typeof checkFrequency === 'number' ? (
    CheckFrequency[checkFrequency]
  ) : (
    <div className="text-gray-400">Pozastaveno</div>
  )

export const lastCheckRenderer = (lastCheck: string) =>
  !lastCheck ? (
    <span className="text-gray-400">Nezkontrolováno</span>
  ) : (
    formatDistanceToNow(new Date(lastCheck), {
      locale: csLocale,
      addSuffix: true,
    })
  )

export const nextCheckRenderer = (_: string, record: RcTableItem) => {
  if (record.checkFrequency === 0 && !record.lastCheck) {
    return <div className="text-gray-400">Do hodiny</div>
  }
  if (record.checkFrequency) {
    return formatDistanceToNow(
      addDays(
        new Date(record.lastCheck || record.createdAt),
        record.checkFrequency
      ),
      {
        locale: csLocale,
        addSuffix: true,
      }
    )
  }
  return <div className="text-gray-400">Pozastaveno</div>
}

export const birthDateRenderer = (birthDate: string, record: RcTableItem) =>
  birthDate ? (
    format(new Date(birthDate), czDateFormatDay)
  ) : record.rc ? (
    <RcNumberData getBirthdate rcNumber={record.rc} className="text-gray-700" />
  ) : (
    <div className="text-gray-400 w-full text-center">---</div>
  )

export const fullnameSorter = <
  T extends {
    lastname?: string | null
    cuzkOByCuzkOsId?: { prijmeni?: string | null } | null
  }
>(
  a: T,
  b: T
): number => {
  const nameA = a.lastname || a.cuzkOByCuzkOsId?.prijmeni || ''
  const nameB = b.lastname || b.cuzkOByCuzkOsId?.prijmeni || ''
  return nameA.localeCompare(nameB)
}

export const fullnameFilter = (value: string, record: RcTableItem) =>
  !!(
    (record.firstname && `${record.firstname} ${record.lastname}`) ||
    (record.cuzkOByCuzkOsId?.prijmeni &&
      `${record.cuzkOByCuzkOsId?.jmeno} ${record.cuzkOByCuzkOsId?.prijmeni}`)
  )
    ?.toString()
    .toLowerCase()
    .includes((value as string).toLowerCase())
